import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { User, LogOut, ChevronDown, Shield } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';

export default function DashboardProfileDropdown({ className }: { className?: string, onLogout?: () => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    const currentPath = window.location.pathname;

    logout();

    if (currentPath.startsWith('/dashboard') || currentPath.startsWith('/admin')) {
      navigate('/');
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={cn('rounded-t-lg p-4 relative', className)} ref={dropdownRef}>
      {user ? (
        <>
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => setIsOpen(!isOpen)}
          >

            <div className="flex items-center gap-2 bg-white rounded-full p-1">
              <User className="h-4 w-4" />
            </div>
            <p className="text-sm text-white">{user?.email}</p>
            <ChevronDown className="h-4 w-4 text-white" />
          </div>

          {isOpen && (
            <div className="absolute right-4 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
              <div className="py-1" role="menu" aria-orientation="vertical">
                {isAdmin && (
                  <>
                    <Link to="/admin" className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      <Shield className="h-4 w-4 mr-2" />
                      Admin Dashboard
                    </Link>
                    <hr className="my-1" />
                  </>
                )}
                <Link to="/dashboard" className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  <User className="h-4 w-4 mr-2" />
                  Dashboard
                </Link>
                <hr className="my-1" />
                <button
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={handleLogout}
                  role="menuitem"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Log out
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <Link to="/login" className="flex items-center gap-2 cursor-pointer">
          <div className="flex items-center gap-2 bg-white rounded-full p-1">
            <User className="h-4 w-4" />
          </div>
          <p className="text-sm text-white">Log in</p>
        </Link>
      )}
    </div>
  );
}
